import * as Abas from "@radix-ui/react-tabs";
import { useSearchParams } from "react-router-dom";
import { useSessao } from "@/sessao/useSessao";
import { useDocumentos, useSaudeDoProvider, useVisaoGeral } from "@/api/consultas";
import { textoDoErro } from "@/api/problema";
import { CaixaDeCampo } from "@/componentes/CaixaDeCampo";
import { Barra } from "@/componentes/Barra";
import type { AbaDaOperacao } from "@/rotas";
import { Auditoria } from "./Auditoria";
import { Entregas } from "./Entregas";
import { FilaMorta } from "./FilaMorta";
import { Divergencias } from "./Divergencias";
import { Indeterminados } from "./Indeterminados";
import { TitulosOrfaos } from "./TitulosOrfaos";
import { contagem, dataHora, faz } from "@/formato";

/*
 * A tela de Operação — doc 06 §4.3.
 *
 * Oito abas, e a aba escolhida vive na URL pelo mesmo motivo dos filtros de
 * Boletos: um operador que encontra algo estranho manda o link, e o outro chega
 * na mesma aba. A consulta fica só com ela ao trocar: filtro é da aba que o aplicou.
 *
 * Aba que a URL pede e esta sessão não tem, como Auditoria para `leitura`, cai na
 * primeira: sem isso, o Radix abriria a tela sem aba nenhuma.
 */
const ABA_INICIAL = "indeterminados";

const ABAS: readonly (readonly [AbaDaOperacao, string])[] = [
  [ABA_INICIAL, "Indeterminados"],
  ["fila-morta", "Fila morta"],
  ["divergencias", "Divergências"],
  ["titulos-orfaos", "Títulos órfãos"],
  ["documentos", "Documentos"],
  ["provider", "Saúde do provider"],
  ["entregas", "Entregas"],
  ["auditoria", "Auditoria"],
];

export function Operacao() {
  const { pode } = useSessao();
  const [parametros, setParametros] = useSearchParams();

  const veAuditoria = pode("operador");
  const abas = veAuditoria ? ABAS : ABAS.filter(([valor]) => valor !== "auditoria");

  const pedida = parametros.get("aba");
  const aba = abas.find(([valor]) => valor === pedida)?.[0] ?? ABA_INICIAL;

  return (
    <section className="flex flex-col gap-3">
      <h1 className="text-titulo font-semibold">Operação</h1>

      <Abas.Root
        value={aba}
        onValueChange={(valor) => setParametros({ aba: valor }, { replace: true })}
      >
        <Abas.List aria-label="Seções de operação" className="flex flex-wrap gap-1 border-b border-fio">
          {abas.map(([valor, rotulo]) => (
            <Abas.Trigger
              key={valor}
              value={valor}
              className="-mb-px border-b-2 border-transparent px-3 py-2 text-corpo text-texto-2 data-[state=active]:border-esferografica data-[state=active]:text-texto"
            >
              {rotulo}
            </Abas.Trigger>
          ))}
        </Abas.List>

        {/*
          `forceMount` NÃO é usado: cada aba só busca quando está aberta. A
          amostra da fila morta abre canal no broker, e a lista de documentos é
          uma consulta a mais — montar as oito de uma vez faria a tela
          conversar com todo mundo para mostrar uma coisa só.
        */}
        <Abas.Content value="indeterminados" className="pt-4">
          <Indeterminados />
        </Abas.Content>

        <Abas.Content value="fila-morta" className="pt-4">
          <FilaMorta />
        </Abas.Content>

        <Abas.Content value="divergencias" className="pt-4">
          <Divergencias />
        </Abas.Content>

        <Abas.Content value="titulos-orfaos" className="pt-4">
          <TitulosOrfaos />
        </Abas.Content>

        <Abas.Content value="documentos" className="pt-4">
          <Documentos />
        </Abas.Content>

        <Abas.Content value="provider" className="pt-4">
          <SaudeDoProvider />
        </Abas.Content>

        <Abas.Content value="entregas" className="pt-4">
          <Entregas />
        </Abas.Content>

        {veAuditoria && (
          <Abas.Content value="auditoria" className="pt-4">
            <Auditoria />
          </Abas.Content>
        )}
      </Abas.Root>
    </section>
  );
}

function Documentos() {
  const { cliente, estado } = useSessao();
  const { data, error, isPending } = useDocumentos(cliente, estado.fase === "dentro");

  if (isPending) return <div aria-hidden="true" className="h-32 rounded-cartao bg-superficie" />;
  if (error) {
    return (
      <p role="alert" className="text-falha">
        {textoDoErro(error)}
      </p>
    );
  }

  const documentos = data?.documentos ?? [];
  if (documentos.length === 0) {
    return (
      <p className="text-texto-2">Nenhum documento em montagem.</p>
    );
  }

  return (
    <div className="grid gap-2 md:grid-cols-2">
      {documentos.map((documento) => {
        const pulados = documento.pulados ?? 0;

        return (
          <article key={documento.id} className="rounded-cartao border border-fio bg-superficie p-3">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-corpo text-texto">{documento.tipo}</span>
              <span className="rotulo">{documento.estado}</span>
            </div>

            <p className="mt-1 text-[12px] text-texto-2">{documento.sistema_nome}</p>

            <div className="mt-2 flex flex-wrap gap-3 text-[12px] text-texto-2">
              <span>
                <span className="dados">{documento.progresso ?? 0}</span>/
                <span className="dados">{documento.total ?? 0}</span> itens
              </span>
              {documento.paginas !== undefined && (
                <span>
                  <span className="dados">{documento.paginas}</span> páginas
                </span>
              )}
              {documento.criado_em !== undefined && <span>há {faz(documento.criado_em)}</span>}
            </div>

            {pulados > 0 && (
              /*
                Um documento "pronto" com itens pulados está INCOMPLETO, e quem
                o baixar vai receber menos do que pediu. Dizer isso aqui é o que
                impede a descoberta acontecer do lado do cliente.
              */
              <p className="mt-2 text-[12px] text-transito">
                {contagem(pulados, "item não entrou", "itens não entraram")} na montagem.
              </p>
            )}

            {documento.motivo_falha ? (
              <p className="mt-2 text-[12px] text-falha">{documento.motivo_falha}</p>
            ) : null}
          </article>
        );
      })}
    </div>
  );
}

function SaudeDoProvider() {
  const { cliente, estado } = useSessao();
  const { data: visao } = useVisaoGeral(cliente, estado.fase === "dentro");
  const { data: saude, error, isPending } = useSaudeDoProvider(cliente, estado.fase === "dentro");

  if (isPending) return <div aria-hidden="true" className="h-32 rounded-cartao bg-superficie" />;
  if (error) {
    return (
      <p role="alert" className="text-falha">
        {textoDoErro(error)}
      </p>
    );
  }

  const circuitos = visao?.circuitos;
  const orcamento = visao?.orcamento;
  const medidaEm = (saude as { medida_em?: string } | undefined)?.medida_em;

  return (
    <div className="flex flex-col gap-4">
      {medidaEm !== undefined && <p className="rotulo">medida em {dataHora(medidaEm)}</p>}

      <section>
        <h2 className="text-titulo font-semibold">Circuitos</h2>
        {circuitos?.estado !== "ok" ? (
          <p className="mt-2 text-texto-2">Não foi possível ler o estado dos circuitos.</p>
        ) : (circuitos.abertos?.length ?? 0) === 0 ? (
          <p className="mt-2 text-texto-2">Nenhum circuito aberto.</p>
        ) : (
          <div className="mt-2 grid gap-2 md:grid-cols-2">
            {circuitos.abertos?.map((circuito) => (
              <CaixaDeCampo
                key={`${circuito.escopo}-${circuito.alvo}`}
                rotulo={`${circuito.escopo} · aberto`}
                dados
              >
                <span className="block truncate" title={circuito.alvo}>
                  {circuito.alvo}
                </span>
                {/*
                  "Aberto desde", e não "aberto agora": o dado tem prazo de
                  validade, e o instante é o que permite julgar se é recente.
                */}
                <span className="block text-[11px] text-texto-3">
                  desde {dataHora(circuito.aberto_desde)}
                </span>
              </CaixaDeCampo>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="text-titulo font-semibold">Orçamento de requisições</h2>
        {orcamento?.estado !== "ok" ? (
          <p className="mt-2 text-texto-2">Não foi possível ler o orçamento.</p>
        ) : (
          <div className="mt-2 flex flex-col gap-2">
            {orcamento.classes?.map((classe) => {
              const capacidade = classe.capacidade ?? 0;
              const disponivel = classe.disponivel ?? 0;
              const usado = Math.max(capacidade - disponivel, 0);

              return (
                <div key={`${classe.escopo}-${classe.classe}`}>
                  <div className="flex items-baseline justify-between text-[12px]">
                    <span className="text-texto-2">
                      {classe.classe || "sem classe"} · {classe.escopo}
                    </span>
                    <span className="dados text-texto-2">
                      {disponivel}/{capacidade} disponível
                    </span>
                  </div>
                  {/*
                    Barra fina, como o doc 06a §4.2 pede. A leitura do orçamento
                    NÃO consome token — medir gastando o que se mede daria um
                    número errado a cada consulta.
                  */}
                  <div className="mt-1">
                    <Barra
                      usado={usado}
                      total={capacidade}
                      rotulo={`Orçamento ${classe.classe || "sem classe"} em ${classe.escopo}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
